import { Controller } from "stimulus"

// Adds a list of classes to the element one at a time, for pictures that
// build step by step. Render the element finished, with every step class
// present, so it reads the same without JavaScript or with reduced motion.
// On connect the steps are cleared, and they come back in order the first
// time the element scrolls into view: the first step after a short lead-in,
// so the build reads as starting rather than loading, then one per interval.
// `play` replays it.
//
//   data-controller="class-sequence"
//   data-class-sequence-steps-value='["is-step-1", "is-step-2"]'
//   data-class-sequence-interval-value="1500"
//   data-class-sequence-lead-in-value="300"
//   data-class-sequence-playing-class="is-playing"
export default class extends Controller {
  static values = { steps: Array, interval: Number, leadIn: Number }
  static classes = ["playing"]

  connect() {
    if (!this.hasStepsValue || this.prefersReducedMotion) return

    this.reset()
    this.startWhenVisible()
  }

  disconnect() {
    this.observer?.disconnect()
    this.stop()
  }

  play() {
    if (!this.hasStepsValue || this.prefersReducedMotion) return

    this.reset()
    this.start()
  }

  reset() {
    this.stop()
    if (this.hasPlayingClass) this.element.classList.add(this.playingClass)
    this.element.classList.remove(...this.stepsValue)
    this.shownSteps = 0
  }

  start() {
    this.stop()
    this.leadInTimer = setTimeout(() => {
      this.showNextStep()
      if (this.shownSteps < this.stepsValue.length) {
        this.timer = setInterval(() => this.showNextStep(), this.interval)
      }
    }, this.leadIn)
  }

  stop() {
    clearTimeout(this.leadInTimer)
    clearInterval(this.timer)
    this.leadInTimer = null
    this.timer = null
  }

  showNextStep() {
    this.element.classList.add(this.stepsValue[this.shownSteps])
    this.shownSteps += 1
    if (this.shownSteps >= this.stepsValue.length) this.stop()
  }

  startWhenVisible() {
    if (!("IntersectionObserver" in window)) return this.start()

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return

      this.observer.disconnect()
      this.start()
    }, { threshold: 0.35 })
    this.observer.observe(this.element)
  }

  get interval() {
    return this.hasIntervalValue ? this.intervalValue : 1500
  }

  get leadIn() {
    return this.hasLeadInValue ? this.leadInValue : 300
  }

  get prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  }
}
